import { Button } from '@/components/ui/button';
import { useState, useEffect, useRef } from 'react';
import {
  Mail, Phone, MapPin, Instagram, Facebook, Twitter, Youtube,
  Linkedin, Music, Calendar, ExternalLink, Play, ChevronRight, ChevronLeft, Star, Quote,
  User, ArrowRight, X, Plus, Briefcase, Users, HelpCircle, MessageSquare, CheckCircle,
  Pause
} from 'lucide-react';

import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { AnimatedSection } from './AnimatedSection';

const THEMES = {
  dark_stage: {
    bg: 'bg-slate-900',
    text: 'text-white',
    textMuted: 'text-slate-400',
    card: 'bg-slate-800',
    border: 'border-slate-700',
    accent: 'text-orange-500',
    button: 'bg-orange-500 hover:bg-orange-400',
  },
  theatre_red: {
    bg: 'bg-[#1a0c0e] tx-velvet',
    text: 'text-[#f8f0e6]',
    textMuted: 'text-[#bfa39a]',
    card: 'bg-[#120709] tx-velvet-alt',
    border: 'border-[#3b2125]',
    accent: 'text-[#d4af37]',
    button: 'bg-[#b91c1c] hover:bg-[#dc2626]',
  },
  ivory_gold: {
    bg: 'bg-[#faf7f1]',
    text: 'text-[#1c1710]',
    textMuted: 'text-[#8d7f68]',
    card: 'bg-[#f1ebdd]',
    border: 'border-[#e2d8c3]',
    accent: 'text-[#b08d2f]',
    button: 'bg-[#1c1710] hover:bg-[#3a2f1e]',
  },
  light_modern: {
    bg: 'bg-white',
    text: 'text-slate-900',
    textMuted: 'text-slate-500',
    card: 'bg-slate-50',
    border: 'border-slate-200',
    accent: 'text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-500',
  },
  colorful_kids: {
    bg: 'bg-amber-50',
    text: 'text-purple-900',
    textMuted: 'text-purple-600',
    card: 'bg-white',
    border: 'border-pink-200',
    accent: 'text-pink-500',
    button: 'bg-pink-500 hover:bg-pink-400',
  },
  elegant_classic: {
    bg: 'bg-stone-900',
    text: 'text-stone-50',
    textMuted: 'text-stone-400',
    card: 'bg-stone-800',
    border: 'border-stone-700',
    accent: 'text-amber-400',
    button: 'bg-amber-500 hover:bg-amber-400',
  },
  bold_artist: {
    bg: 'bg-zinc-900',
    text: 'text-white',
    textMuted: 'text-zinc-400',
    card: 'bg-zinc-800',
    border: 'border-zinc-700',
    accent: 'text-red-500',
    button: 'bg-red-500 hover:bg-red-400',
  },
};

// Social icon mapper
const getSocialIcon = (platform) => {
  const name = platform.toLowerCase();
  if (name.includes('instagram')) return Instagram;
  if (name.includes('facebook')) return Facebook;
  if (name.includes('twitter') || name.includes('x')) return Twitter;
  if (name.includes('youtube')) return Youtube;
  if (name.includes('linkedin')) return Linkedin;
  if (name.includes('spotify') || name.includes('music')) return Music;
  return ExternalLink;
};


export const ServicesSection = ({ data, theme, primaryColor }) => {
  const t = THEMES[theme] || THEMES.dark_stage;
  const services = data?.services || [];
  const layout = data?.layout || 'grid';
  
  if (services.length === 0) return null;

  const renderGridLayout = () => (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {services.map((service, idx) => (
        <AnimatedSection
          key={service.id || idx}
          animation="fadeUp"
          delay={idx * 100}
          className={cn("rounded-2xl border overflow-hidden", t.bg, t.border)}
        >
          {service.image && (
            <div className="aspect-video overflow-hidden">
              <img src={service.image} alt={service.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
          )}
          <div className="p-6">
            <h3 className={cn("text-xl font-bold mb-2", t.text)}>{service.title}</h3>
            <p className={cn("text-sm mb-4 line-clamp-3", t.textMuted)}>{service.description}</p>
            <div className="flex items-center justify-between">
              <div>
                {service.price && <span className="font-semibold" style={{ color: primaryColor }}>{service.price}</span>}
                {service.duration && <span className={cn("text-sm ml-2", t.textMuted)}>• {service.duration}</span>}
              </div>
              {service.link && (
                <Button size="sm" style={{ backgroundColor: primaryColor }} className="text-white" onClick={() => window.open(service.link, '_blank')}>
                  Book Now
                </Button>
              )}
            </div>
          </div>
        </AnimatedSection>
      ))}
    </div>
  );

  const renderListLayout = () => (
    <div className="space-y-4 max-w-4xl mx-auto">
      {services.map((service, idx) => (
        <AnimatedSection
          key={service.id || idx}
          animation="slideRight"
          delay={idx * 100}
          className={cn("rounded-xl border p-6 flex gap-6", t.bg, t.border)}
        >
          {service.image && (
            <div className="w-32 h-24 rounded-lg overflow-hidden flex-shrink-0">
              <img src={service.image} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="flex-1">
            <h3 className={cn("text-lg font-bold", t.text)}>{service.title}</h3>
            <p className={cn("text-sm mt-2 whitespace-pre-line leading-relaxed", t.textMuted)}>{service.description}</p>
            <div className="flex items-center gap-4 mt-3">
              {service.price && <span className="font-semibold" style={{ color: primaryColor }}>{service.price}</span>}
              {service.duration && <span className={cn("text-sm", t.textMuted)}>{service.duration}</span>}
            </div>
          </div>
          {service.link && (
            <Button style={{ backgroundColor: primaryColor }} className="text-white self-center" onClick={() => window.open(service.link, '_blank')}>
              Book
            </Button>
          )}
        </AnimatedSection>
      ))}
    </div>
  );

  const renderCardsLayout = () => (
    <div className="grid md:grid-cols-2 gap-8">
      {services.map((service, idx) => (
        <AnimatedSection
          key={service.id || idx}
          animation="scale"
          delay={idx * 100}
          className={cn("rounded-2xl border p-8 text-center", t.bg, t.border)}
        >
          {service.image && (
            <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden">
              <img src={service.image} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <h3 className={cn("text-xl font-bold mb-2", t.text)}>{service.title}</h3>
          <p className={cn("text-sm mb-4", t.textMuted)}>{service.description}</p>
          {service.price && <p className="text-2xl font-bold mb-4" style={{ color: primaryColor }}>{service.price}</p>}
          {service.link && (
            <Button style={{ backgroundColor: primaryColor }} className="text-white" onClick={() => window.open(service.link, '_blank')}>
              Book Now
            </Button>
          )}
        </AnimatedSection>
      ))}
    </div>
  );

  const renderTimetableLayout = () => {
    const pricing = services.filter(s => s.prices?.length);
    const scheduled = services.filter(s => s.schedule?.length);
    return (
      <div className="space-y-8">
        {pricing.map((service, idx) => (
          <AnimatedSection key={service.id || idx} animation="fadeUp" className={cn("rounded-3xl border p-8 md:p-10", t.bg, t.border)}>
            <div className="flex flex-col lg:flex-row lg:items-center gap-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 flex-1">
                {service.prices.map((p, i) => (
                  <div key={i} className="text-center">
                    <p className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ color: primaryColor }}>{p.price}</p>
                    <p className={cn("text-xs md:text-sm mt-1.5 font-medium", t.textMuted)}>{p.label}</p>
                  </div>
                ))}
              </div>
              <div className="lg:max-w-xs lg:border-l lg:pl-8 space-y-3" style={{ borderColor: primaryColor + '30' }}>
                {service.badge && (
                  <span className="inline-block text-xs font-bold uppercase tracking-wider text-white px-3.5 py-1.5 rounded-full" style={{ backgroundColor: primaryColor }}>
                    {service.badge}
                  </span>
                )}
                {service.note && <p className={cn("text-xs leading-relaxed", t.textMuted)}>{service.note}</p>}
              </div>
            </div>
          </AnimatedSection>
        ))}
        <div className="grid lg:grid-cols-3 gap-6 items-start">
          {scheduled.map((service, idx) => (
            <AnimatedSection key={service.id || idx} animation="fadeUp" delay={idx * 120} className={cn("rounded-3xl border overflow-hidden", t.bg, t.border)}>
              <div className="p-6 pb-5" style={{ backgroundColor: primaryColor + '12' }}>
                <div className="flex items-start justify-between gap-3">
                  <h3 className={cn("text-xl font-bold", t.text)}>{service.title}</h3>
                  {service.price && (
                    <span className="text-xs font-bold whitespace-nowrap px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: primaryColor }}>{service.price}</span>
                  )}
                </div>
                {service.description && <p className={cn("text-xs mt-2 leading-relaxed", t.textMuted)}>{service.description}</p>}
              </div>
              <div className="p-6 pt-4 space-y-5">
                {service.schedule.map((group, gi) => (
                  <div key={gi}>
                    <div className="flex items-center gap-2 mb-2.5">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: primaryColor }} />
                      <p className="text-xs font-bold uppercase tracking-[0.15em]" style={{ color: primaryColor }}>{group.day}</p>
                      <div className={cn("flex-1 border-t", t.border)} />
                    </div>
                    <div className="space-y-2">
                      {group.entries.map((entry, ei) => (
                        <div key={ei} className="flex items-center justify-between gap-3">
                          <p className={cn("text-sm leading-snug", t.text)}>{entry.name}</p>
                          <span className="text-xs font-semibold whitespace-nowrap px-2.5 py-1 rounded-full" style={{ backgroundColor: primaryColor + '15', color: primaryColor }}>
                            {entry.time}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    );
  };

  return (
    <section id="services" className={cn("py-24 px-4", t.card)}>
      <div className="max-w-6xl mx-auto">
        <AnimatedSection animation="fadeUp" className="text-center mb-16">
          <span className="text-sm uppercase tracking-widest font-semibold mb-4 block" style={{ color: primaryColor }}>{data?.eyebrow || 'What I Offer'}</span>
          <h2 className={cn("text-4xl md:text-5xl font-bold", t.text)} style={{ fontFamily: "'Outfit', sans-serif" }}>{data.title || 'Services'}</h2>
        </AnimatedSection>
        {layout === 'timetable' && renderTimetableLayout()}
        {layout === 'list' && renderListLayout()}
        {layout === 'grid' && renderGridLayout()}
        {layout === 'cards' && renderCardsLayout()}
      </div>
    </section>
  );
};


